import React, { useMemo, useState } from "react";
import { Box, Paper, Typography, IconButton, Grid, Chip } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

const toDateKey = (date) => new Date(new Date(date).toDateString()).getTime();
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const buildMonthGrid = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
};

const VehicleHiringEntryDatesPicker = ({ value = [], onChange, error, helperText }) => {
    const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));

    const selectedKeys = useMemo(
        () => new Set((value || []).map((d) => toDateKey(d))),
        [value]
    );

    const cells = useMemo(() => buildMonthGrid(currentMonth), [currentMonth]);

    const sortedSelected = useMemo(
        () => [...(value || [])].sort((a, b) => new Date(a) - new Date(b)),
        [value]
    );

    const handleToggle = (date) => {
        const key = toDateKey(date);
        if (selectedKeys.has(key)) {
            onChange((value || []).filter((d) => toDateKey(d) !== key));
        } else {
            onChange([...(value || []), date]);
        }
    };

    return (
        <Box>
            <Paper variant="outlined" sx={{ p: 2, maxWidth: 420 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <IconButton
                        size="small"
                        onClick={() =>
                            setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
                        }
                    >
                        <ChevronLeft />
                    </IconButton>
                    <Typography fontWeight={600}>
                        {currentMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() =>
                            setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
                        }
                    >
                        <ChevronRight />
                    </IconButton>
                </Box>
                <Grid container columns={7} spacing={0.5}>
                    {WEEKDAY_LABELS.map((label) => (
                        <Grid item xs={1} key={label}>
                            <Typography variant="caption" color="text.secondary" align="center" display="block">
                                {label}
                            </Typography>
                        </Grid>
                    ))}
                    {cells.map((date, idx) => {
                        if (!date) return <Grid item xs={1} key={`blank-${idx}`} />;
                        const key = toDateKey(date);
                        const isSelected = selectedKeys.has(key);
                        return (
                            <Grid item xs={1} key={key}>
                                <Box
                                    onClick={() => handleToggle(date)}
                                    sx={{
                                        aspectRatio: "1 / 1",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: 1,
                                        fontSize: "0.8rem",
                                        cursor: "pointer",
                                        bgcolor: isSelected ? "primary.main" : "action.hover",
                                        color: isSelected ? "common.white" : "text.primary",
                                        "&:hover": { opacity: 0.8 },
                                    }}
                                >
                                    {date.getDate()}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>
            <Box mt={1}>
                <Typography variant="caption" color="text.secondary">
                    {sortedSelected.length} day{sortedSelected.length !== 1 ? "s" : ""} selected
                    {sortedSelected.length > 0 &&
                        ` — ${sortedSelected
                            .map((d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }))
                            .join(", ")}`}
                </Typography>
            </Box>
            {error && (
                <Typography variant="caption" color="error" display="block" mt={0.5}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};

export default VehicleHiringEntryDatesPicker;